import React from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Eye, EyeOff } from 'lucide-react';

interface OverlayStrengthSelectorProps {
  value: number;
  onChange: (strength: number) => void;
}

/**
 * OverlayStrengthSelector - A slider for controlling overlay opacity strength
 */
const OverlayStrengthSelector: React.FC<OverlayStrengthSelectorProps> = ({ 
  value, 
  onChange 
}) => {
  const handleSliderChange = (values: number[]) => {
    onChange(values[0]);
  };

  const getStrengthLabel = (strength: number) => {
    if (strength <= 0.2) return 'Very Light';
    if (strength <= 0.4) return 'Light';
    if (strength <= 0.6) return 'Medium';
    if (strength <= 0.8) return 'Strong';
    return 'Very Strong';
  };

  const getStrengthColor = (strength: number) => {
    if (strength <= 0.2) return 'text-blue-500';
    if (strength <= 0.4) return 'text-green-500';
    if (strength <= 0.6) return 'text-yellow-500';
    if (strength <= 0.8) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Overlay Strength</Label>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${getStrengthColor(value)}`}>
            {getStrengthLabel(value)}
          </span>
          <span className="text-xs text-gray-500">
            ({Math.round(value * 100)}%)
          </span>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <EyeOff className="h-4 w-4 text-gray-400" />
          <Slider
            value={[value]}
            onValueChange={handleSliderChange}
            max={1}
            min={0.05}
            step={0.05}
            className="flex-1"
          />
          <Eye className="h-4 w-4 text-gray-600" />
        </div>
        
        <div className="text-xs text-gray-500 text-center">
          Controls the opacity of gradient overlays on menu item images
        </div>
      </div>
    </div>
  );
};

export default OverlayStrengthSelector;